import React, { ReactElement, useCallback, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { createSelector } from 'reselect'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Drawer from '@material-ui/core/Drawer'
import Box from '@material-ui/core/Box'
import WorkspaceGate from './../../gate/workspace/'
import Loader from './../../loader/'
import Hidden from '@material-ui/core/Hidden'
import Button from '@material-ui/core/Button'
import Breadcrumbs from '@material-ui/core/Breadcrumbs'
import Typography from '@material-ui/core/Typography'
import Link from '@material-ui/core/Link'
import TopBar from './../../top-bar/'
import { Connected as Nav } from './../../nav/'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { Connected as MobileWorkspaceSelector } from './../../workspace-selector/mobile'
import { Connected as WebWorkspaceSelector } from './../../workspace-selector/web'
import { navItems } from 'application/routes'
import AuthedLayout from './../authed/'
import Alert from '@material-ui/lab/Alert'
import LinearProgress from '@material-ui/core/LinearProgress'
import { impersonate } from 'actions/user'
import { I18n } from '@aws-amplify/core'
import util from 'util'
import Container from '@material-ui/core/Container'

const I18N_NS = 'layout.default'

I18n.putVocabularies({
  en: {
    [`${I18N_NS}.impersonate.switchBack`]: 'Switch Back',
    [`${I18N_NS}.impersonate.detail`]: 'You are currently impersonating the following company: %s'
  }
})

/**
 * Fetch impersonated company details
 */
const fetchImpersonated = createSelector(
  (state: any) => state._user.impersonate,
  (state: any) => state.auth.companyId,
  (impersonate, companyId) => {
    if (!impersonate || !impersonate.companyId) return null

    return (parseInt(companyId, 10) !== (impersonate?.companyId) ? impersonate : null)
  }
)

export const generateCrumbs = (path, title, description, mobile, large) => {
  let current: any = null
  let currentPath = path
  const stack: any[] = []

  const match = (i) => ((typeof i.path !== 'undefined' && i.path === currentPath) || i.id === currentPath)

  do {
    current = navItems.find(match)
    current && stack.push(current)
    currentPath = current?.parent
  } while (currentPath)

  stack.reverse()

  // Runtime error, crumbs cannot be automatically generated
  if (!stack.length) return <Box>Unable to autogenerate crumbs...</Box>

  // Override last entry manually
  if (title) stack.push({ label: title })

  return (
    <Box>
      {!mobile &&
        <Breadcrumbs>
          {stack.map((item, index) => {
            if (index === (stack.length - 1)) { /* Last crumb in the trail */
              return <Typography color="textPrimary" variant={large ? 'h6' : 'subtitle2'} key={`crumb-${index}`}>{item.label}</Typography>
            } else if (!item.path) { /* Crumb trail without a link */
              return <Typography color="textSecondary" variant={large ? 'h6' : 'subtitle1'} key={`crumb-${index}`}>{item.label}</Typography>
            } else { /* Linkable crumb trail */
              return <Link color="inherit" href={item.path} variant={large ? 'h6' : 'subtitle1'} key={`crumb-${index}`}>{item.label}</Link>
            }
          })}
        </Breadcrumbs>}
    </Box>
  )
}

export const CrumbTitle = ({ pathname, title, description, mobile, large = false }): ReactElement => {
  return generateCrumbs(pathname, title, description, mobile, large)
}

const CrumbTopBar = ({ pathname }) => {
  return (
    <Box height="100%" p={4} alignItems="center" display="flex">
      <CrumbTitle
        pathname={pathname || '/'}
        mobile={false}
        large={true}
        title={null}
        description={null} />
    </Box>
  )
}

interface DefaultLayoutProps {
  disableFilter?: boolean
  pathname?: string
  companyId?: number
  children: any
  renderActions?: any
  renderTitle?: any
  padding?: number
  progress?: boolean
  hideToolbar?: boolean
  useCrumbTopBar?: boolean
  contain?: boolean
  none?: boolean
  /** Only display page contains and hide layout */
  hide?: boolean
}

const DefaultLayout = ({
  disableFilter, pathname, companyId, children, renderActions, renderTitle, padding, progress, hideToolbar, useCrumbTopBar = false, contain = false, none = false, hide = false
}: DefaultLayoutProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const impersonated = useSelector(fetchImpersonated)
  const onSwitchBack = useCallback(() => dispatch(impersonate(null, null, null)), [dispatch])

  const [mobileMenu, setMobileMenu] = useState(false)
  const smDown = useMediaQuery((theme: any) => theme?.breakpoints?.down('sm'))

  renderTitle = renderTitle
    ? renderTitle()
    : <CrumbTitle
      pathname={pathname ?? '/'}
      mobile={smDown}
      title={null}
      description={null} />

  pathname = useSelector((state: any) => pathname ?? state.router?.location?.pathname)

  // Wrap the layout in an "auth gate". User should not be able to view
  // the main application without being authorized
  return (
    <React.Fragment>
      <AuthedLayout>
        <WorkspaceGate loader={<Loader />}>
          {!hide &&
            <>
              <Box displayPrint="none">
                <TopBar
                  mobileTitle={renderTitle}
                  onMenuToggle={() => setMobileMenu(!mobileMenu)}
                  disableFilter={disableFilter}
                  showBackButton={mobileMenu}
                  workspaceSelector={none
                    ? undefined
                    : useCrumbTopBar
                      ? <CrumbTopBar pathname={pathname} />
                      : <WebWorkspaceSelector disableFilter={disableFilter} />} />
              </Box>

              <Box displayPrint="none">
                <Drawer
                  classes={{ paper: classes.drawerPaper }}
                  variant="persistent"
                  open={!smDown || mobileMenu}>
                  {/* Render the mobile workspace menu only on smaller devices */}
                  <Hidden smUp>
                    <MobileWorkspaceSelector />
                  </Hidden>
                  <Nav />
                </Drawer>
              </Box>

              <Box flex={1} position='relative'>
                <main className={clsx(classes.content, classes.contentShift)} id="innerContent">
                  {/* 80 is the height with included IconButton */}
                  {!hideToolbar &&
                    <Box bgcolor="background.bg1" p={2} px={4} width="100%" display="flex" alignItems="center" position="relative" displayPrint="none" minHeight={80}>
                      <Box flexGrow={1}>
                        <Hidden smDown>{renderTitle}</Hidden>
                      </Box>
                      <Box display="flex" alignItems="center" justifyContent="center">
                        {renderActions?.()}
                      </Box>
                      {progress && <LinearProgress color="secondary" className={classes.progress} />}
                    </Box>}

                  {Boolean(impersonated) && false &&
                    <Box bgcolor="background.paper">
                      <Alert
                        severity="warning"
                        className={classes.alert}
                        action={
                          <Button color="inherit" size="small" onClick={onSwitchBack}>
                            {I18n.get(`${I18N_NS}.impersonate.switchBack`)}
                          </Button>
                        }>
                        {util.format(I18n.get(`${I18N_NS}.impersonate.detail`), impersonated.name)}
                      </Alert>
                    </Box>}

                  <Box flexGrow={1} className={classes.children} p={typeof padding === 'undefined' ? 2 : padding} position={hideToolbar ? 'relative' : null}>
                    {progress && hideToolbar && <LinearProgress color="secondary" className={classes.progress} />}
                    {contain
                      ? <Container maxWidth="xl">{children}</Container>
                      : children}
                  </Box>
                </main>
              </Box>
            </>
            ||
            <Box flexGrow={1} className={classes.children} p={typeof padding === 'undefined' ? 2 : padding} position={hideToolbar ? 'relative' : null}>
              {progress && hideToolbar && <LinearProgress color="secondary" className={classes.progress} />}
              {contain
                ? <Container maxWidth="xl">{children}</Container>
                : children}
            </Box>
          }
        </WorkspaceGate>
      </AuthedLayout>
    </React.Fragment>
  )
}

DefaultLayout.propTypes = {
  children: PropTypes.any.isRequired,
  renderTitle: PropTypes.func,
  renderActions: PropTypes.func
}

export default DefaultLayout

export const Connected = (props) => {
  props = {
    ...props,
    pathname: useSelector((state: any) => state.router?.location?.pathname)
  }

  return <DefaultLayout {...props} />
}

const useStyles = makeStyles(theme => ({
  '@global': {
    body: {
      height: '100%'
    },
    '#root': {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      alignContent: 'stretch'
    }
  },
  children: {
    flex: 1,
    alignItems: 'stretch',
    width: '100%'
  },
  content: {
    marginTop: theme.toolbarHeight,
    height: `calc(100% - ${theme.toolbarHeight}px)`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    alignContent: 'stretch',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  contentShift: {
    width: `calc(100% - ${theme.drawerWidth}px)`,
    marginLeft: theme.drawerWidth,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginLeft: 0
    }
  },
  progress: {
    zIndex: 10,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0
  },
  drawerPaper: {
    width: theme.drawerWidth,
    // Remove the default paper border
    borderTop: 0,
    borderRight: 0,
    // Let the navigation (by default) sit below the top bar
    paddingTop: theme.toolbarHeight,
    // Show below app bar
    zIndex: theme.zIndex.appBar - 1,
    backgroundColor: theme.palette.background.bg2
  },
  alert: {
    borderRadius: 0
  }
}))
